import { useCallback, useEffect, useRef, useState } from 'react';
import { api, ApiError } from './api';
import type { User } from './types';
import { Login } from './views/Login';
import { Register } from './views/Register';
import { Onboarding } from './views/Onboarding';
import { Mail } from './views/Mail';
import { Recover, type RecoverMode } from './views/Recover';

type State =
  | { status: 'loading' }
  | { status: 'anonymous'; view: 'login' | 'register' | 'forgot' }
  | { status: 'authenticated'; user: User };

/**
 * Ödəniş şlüzündən qayıdışın nəticəsi.
 *
 * Dəyərlər panel-api → `/api/billing/yigim/qayidis` ucunun yönləndirdiyi
 * ünvandan gəlir: `/?bolme=abune&odenis=<ugurlu|ugursuz|gozleyir>`.
 * Adlar Azərbaycanca yazılıb ki, müştərinin ünvan sətrində görəcəyi mətn
 * də anlaşılan olsun.
 */
type PaymentOutcome = 'ugurlu' | 'ugursuz' | 'gozleyir';

/**
 * Ünvan sətrindən bərpa keçidini oxuyur.
 *
 * Marşrutlaşdırma kitabxanası QƏSDƏN İŞLƏDİLMİR. Panelin cəmi iki ictimai
 * yolu var (`/reset/<token>`, `/verify/<token>`) və hər ikisi bir dəfə açılıb
 * bağlanır. Bunun üçün router asılılığı gətirmək 40 KB-lıq yükdür.
 *
 * Caddy bütün naməlum yolları `index.html`-ə yönləndirir, ona görə bu yollar
 * serverdə ayrıca qeydiyyat tələb etmir (bax: infra/caddy/Caddyfile).
 */
function readRecoverPath(): RecoverMode | null {
  const m = /^\/(reset|verify)\/([A-Za-z0-9_-]{16,128})\/?$/.exec(window.location.pathname);
  if (!m) return null;
  return m[1] === 'reset'
    ? { kind: 'reset', token: m[2]! }
    : { kind: 'verify', token: m[2]! };
}

/**
 * Ünvan sətrindən ödəniş nəticəsini oxuyur.
 *
 * ⚠️ BU DƏYƏRƏ ETİBAR EDİLİR, AMMA ONA HEÇ NƏ BAĞLI DEYİL. Parametri
 * istifadəçi əl ilə də yaza bilər — ona görə o, YALNIZ MƏTN göstərir.
 * Abunə vəziyyəti həmişə serverdən (`api.billing()` / `api.me()`) oxunur;
 * «ugurlu» yazmaqla heç kim abunə açmır.
 *
 * ⚠️ NAMƏLUM DƏYƏR `null` SAYILIR. Şlüz sabah başqa söz göndərsə, ekranda
 * naməlum kod görünməsin — susmaq yanlış mesajdan yaxşıdır.
 */
function readPaymentReturn(): PaymentOutcome | null {
  const q = new URLSearchParams(window.location.search);
  if (q.get('bolme') !== 'abune') return null;
  const o = q.get('odenis');
  return o === 'ugurlu' || o === 'ugursuz' || o === 'gozleyir' ? o : null;
}

/** «gozleyir» halında neçə dəfə və hansı aralıqla yenidən soruşulur. */
const RECHECK_EVERY_MS = 5000;
const RECHECK_TIMES = 3;

export function App(): React.ReactElement {
  const [state, setState] = useState<State>({ status: 'loading' });

  /*
    ⚠️ ÖDƏNİŞ PARAMETRİ BƏRPA KEÇİDİNDƏN ƏVVƏL OXUNUR.
    Aşağıdakı `recover` başlanğıcı ünvanı `/`-ə qaytarır və bununla
    sorğu sətrini də silir. Praktikada iki hal üst-üstə düşmür (biri
    `/reset/...`, digəri `/?bolme=...`), amma sıradan asılı kod gec-tez
    sınır — ona görə oxu buraya, silmədən ƏVVƏLƏ qoyulub.
  */
  const [pay, setPay] = useState<PaymentOutcome | null>(() => {
    const p = readPaymentReturn();
    // ⚠️ ÜNVAN DƏRHAL TƏMİZLƏNİR. Səbəb bərpa tokenindəki ilə eynidir:
    // istifadəçi səhifəni yeniləyəndə «ödəniş uğurlu» zolağı yenidən
    // görünməməlidir — o, artıq baş vermiş hadisənin xəbəridir və
    // təkrarlanması müştəriyə ikinci ödəniş olduğunu düşündürür.
    if (p) window.history.replaceState(null, '', '/');
    return p;
  });

  // ⚠️ İlk render-də oxunur və DƏRHAL ünvan sətrindən silinir.
  //
  // İki səbəb: (1) token brauzer tarixçəsində, Referer başlığında və
  // paylaşılan ekran görüntüsündə qalmasın; (2) istifadəçi səhifəni
  // yeniləyəndə artıq işlədilmiş token ikinci dəfə göndərilib «used»
  // xətası verməsin.
  const [recover, setRecover] = useState<RecoverMode | null>(() => {
    const r = readRecoverPath();
    if (r) window.history.replaceState(null, '', '/');
    return r;
  });

  /**
   * «gozleyir» yoxlamaları bitib, nəticə isə hələ də müsbət deyil.
   *
   * ⚠️ AYRICA VƏZİYYƏT LAZIMDIR, ÇÜNKİ VƏD MÜDDƏTLİDİR. Zolaqda «bir neçə
   * saniyəyə yenilənəcək» yazılıb; 15 saniyədən sonra da eyni cümlə
   * qalarsa, o, yalan olur və müştəri ekrana baxıb gözləməyə davam edir.
   */
  const [waitDone, setWaitDone] = useState(false);

  /** Abunə bölməsi bir dəfə açılıb — təkrar açılmasın (aşağıya bax). */
  const opened = useRef(false);

  // Səhifə yenilənəndə sessiya cookie-si hələ etibarlıdırsa istifadəçini
  // yenidən giriş etməyə məcbur etmirik.
  useEffect(() => {
    let cancelled = false;
    api
      .me()
      .then(({ user }) => {
        if (!cancelled) setState({ status: 'authenticated', user });
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        // 401 gözləniləndir (sessiya yoxdur). Şəbəkə xətasında da giriş
        // ekranı göstəririk — istifadəçi yenidən cəhd edə bilər.
        if (!(err instanceof ApiError) || err.status === 401) {
          setState({ status: 'anonymous', view: 'login' });
        } else {
          setState({ status: 'anonymous', view: 'login' });
        }
      });
    return () => {
      cancelled = true;
    };
  }, []);

  /*
    ÖDƏNİŞ TƏSDİQLƏNDİ — İSTİFADƏÇİ YENİDƏN OXUNUR.

    ⚠️ ZOLAĞI DƏYİŞMƏK KİFAYƏT ETMİR. Poçt ekranındakı «Poçt hələ
    işləmir» xəbərdarlığı `user.tenantStatus`-dan gəlir və o, sessiya
    başlayanda oxunub. Yeniləməsək, ödəniş etmiş müştəri hələ də qırmızı
    zolaq görər və pulunun getmədiyini düşünüb ikinci dəfə ödəyər.
  */
  useEffect(() => {
    if (pay !== 'ugurlu') return;
    void api
      .me()
      .then(({ user }) => setState({ status: 'authenticated', user }))
      // Sessiya 3DS zamanı bitibsə 401 gəlir — yuxarıdakı boot effekti
      // onsuz da giriş ekranını göstərəcək, burada susuruq.
      .catch(() => undefined);
  }, [pay]);

  /*
    «GÖZLƏYİR» — 3DS-DƏN SONRA STATUS DƏRHAL YENİLƏNMİR.

    ⚠️ NİYƏ AYRICA HAL LAZIMDIR. Bank cavabı bəzən bizim qayıdış ucumuzdan
    gec gəlir: müştəri artıq paneldədir, pul isə hələ «yolda»dır. Bu anı
    «uğursuz» yazmaq ən pis mesajdır — adam ikinci dəfə ödəyir və biz iki
    ödənişi əl ilə geri qaytarırıq.

    ⚠️ SONSUZ SORĞU YOXDUR — CƏMİ 3 DƏFƏ. Ödəniş hələ də təsdiqlənməyibsə,
    onu serverdəki uzlaşdırma işi 10 dəqiqədən bir onsuz da yenidən
    soruşur (payments.ts → reconcilePending). Brauzeri saatlarla sorğu
    göndərməyə qoymağın faydası yoxdur, zərəri isə var: açıq qalmış tab
    API-ni boş yerə yükləyir.
  */
  useEffect(() => {
    if (pay !== 'gozleyir') return;

    let tries = 0;
    let stopped = false;
    const timer = window.setInterval(() => {
      tries += 1;
      const sonuncu = tries >= RECHECK_TIMES;
      if (sonuncu) window.clearInterval(timer);

      void api
        .billing()
        .then((r) => {
          if (stopped) return;
          // ⚠️ YALNIZ MÜSBƏT NƏTİCƏ ZOLAĞI DƏYİŞİR. Status hələ `unpaid`
          // qalıbsa, «uğursuz» yazmırıq: ödəniş sadəcə hələ tətbiq
          // olunmayıb ola bilər.
          if (r.billing.status === 'active' || r.billing.status === 'trial') {
            setPay('ugurlu');
            window.clearInterval(timer);
          } else if (sonuncu) {
            setWaitDone(true);
          }
        })
        // Uc yalnız şirkət admininə açıqdır; adi qutu sahibi 403 alır.
        // Bu, xəta deyil — sadəcə yoxlama onun üçün mənasızdır.
        .catch(() => {
          if (!stopped && sonuncu) setWaitDone(true);
        });
    }, RECHECK_EVERY_MS);

    return () => {
      stopped = true;
      window.clearInterval(timer);
    };
  }, [pay]);

  /*
    ABUNƏ BÖLMƏSİ AVTOMATİK AÇILIR.

    ⚠️ BÖLMƏ VƏZİYYƏTİ BURADA DEYİL — `Mail.tsx`-dədir
    (`useState<Section>('mail')`). Bu komponent yalnız «kim daxil olub»
    sualını bilir; hansı bölmənin açıq olduğunu yox. Ona görə keçid
    yan qapıdan edilir: sol lentdəki «Abunə» düyməsinə proqramla basılır.
    Düymə real, görünən və istifadəçinin özünün də basdığı elementdir —
    yəni burada heç bir gizli davranış yaranmır.

    ⚠️ TAPILMASA HEÇ NƏ OLMUR — VƏ BU, QƏSDƏNDİR. Düymə yoxdursa
    (istifadəçi şirkət admini deyil, qutusu hələ yaradılmayıb, ya da
    lentin etiketi dəyişib) zolaq yenə görünür və müştəri bölməni əl ilə
    aça bilir. Yəni ən pis hal «bir klik artıq»dır, ağ ekran deyil.

    ⚠️ BUNU DÜZGÜN HƏLL ETMƏYİN YOLU `Mail.tsx`-ə başlanğıc bölmə
    parametri əlavə etməkdir (bir sətir). Bu fayl dəsti ilə həmin fayla
    toxunulmadığı üçün körpü burada saxlanılıb; `Mail.tsx` açılan kimi
    həmin parametr verilməli və bu effekt SİLİNMƏLİDİR.
  */
  useEffect(() => {
    if (!pay || opened.current) return;
    if (state.status !== 'authenticated') return;

    let tries = 0;
    const timer = window.setInterval(() => {
      tries += 1;
      const btn = document.querySelector<HTMLButtonElement>(
        '.rail-nav button[aria-label="Abunə"]',
      );
      if (btn) {
        opened.current = true;
        btn.click();
      }
      if (btn || tries >= 10) window.clearInterval(timer);
    }, 80);

    return () => window.clearInterval(timer);
  }, [pay, state.status]);

  const handleAuthed = useCallback((user: User) => {
    setState({ status: 'authenticated', user });
  }, []);

  const handleLogout = useCallback(() => {
    void api.logout().finally(() => setState({ status: 'anonymous', view: 'login' }));
  }, []);

  // Poçt qutusu yaradıldı — istifadəçini yenidən yükləyirik ki, `mailboxEmail`
  // dolsun və panel onboarding-dən poçt ekranına keçsin. Sessiya dəyişmir,
  // yalnız istifadəçinin vəziyyəti yenilənir.
  const handleMailboxCreated = useCallback(() => {
    void api
      .me()
      .then(({ user }) => setState({ status: 'authenticated', user }))
      .catch(() => setState({ status: 'anonymous', view: 'login' }));
  }, []);

  /*
    ÖDƏNİŞ ZOLAĞI.

    ⚠️ ÜST QATDA (`position: fixed`), AXININ İÇİNDƏ DEYİL. Poçt ekranı
    tam hündürlüklü griddir; ora adi element əlavə etmək düzümü sındırır.
    Üstəlik zolaq hansı ekranda olursan ol görünməlidir: müştəri şlüzdən
    qayıdanda onboarding-də, poçtda, hətta giriş ekranında ola bilər.

    ⚠️ BAĞLAMA DÜYMƏSİ VAR. Zolaq özü yox olmur — «uğurlu» halında da
    qalır, çünki müştəri qayıdışdan dərhal sonra ekrana baxmaya bilər.
  */
  const payBanner = pay ? (
    <div
      className={`pay-toast ${
        pay === 'ugurlu' ? 'is-ok' : pay === 'ugursuz' ? 'is-bad' : 'is-wait'
      }`}
      role={pay === 'ugursuz' ? 'alert' : 'status'}
    >
      <div className="pay-toast-text">
        {pay === 'ugurlu' ? (
          <>
            <strong>Ödəniş qeydə alındı.</strong> Abunə uzadıldı — poçtun qəbulu
            və göndərişi açıqdır. Təfərrüatı «Abunə» bölməsindəki ödəniş
            tarixçəsində görə bilərsiniz.
          </>
        ) : pay === 'ugursuz' ? (
          <>
            <strong>Ödəniş tamamlanmadı.</strong> Abunə dəyişmədi. Yenidən cəhd
            edə və ya faktura ilə ödəyə bilərsiniz. Kartınızdan məbləğ
            çıxılıbsa, bizə yazın — ödənişi referans nömrəsi ilə yoxlayaq.
          </>
        ) : waitDone ? (
          /*
            ⚠️ ÜÇ YOXLAMA BİTDİ, TƏSDİQ GƏLMƏDİ. Burada «uğursuz» yazmaq
            olmaz: bank cavabı hələ yolda ola bilər və serverdəki
            uzlaşdırma işi 10 dəqiqədən bir onsuz da yenidən soruşur.
            Müştəriyə lazım olan tək şey — nə etməli olmadığını bilməkdir.
          */
          <>
            <strong>Təsdiq hələ gəlməyib.</strong> Bank cavabı bəzən gecikir —
            abunə təsdiq gələn kimi öz-özünə uzanır, ikinci dəfə ödəməyə
            ehtiyac yoxdur. Vəziyyəti «Abunə» bölməsindəki ödəniş
            tarixçəsindən izləyə bilərsiniz.
          </>
        ) : (
          <>
            {/*
              ⚠️ «İKİNCİ DƏFƏ ÖDƏMƏYİN» CÜMLƏSİ BURADA DA OLMALIDIR.
              Əvvəl o, yalnız üç təkrar yoxlamadan SONRAKI mətndə vardı.
              Halbuki ikiqat ödəniş riski məhz BU ANDA ən yüksəkdir: adam
              indicə kartını təsdiqləyib, ekranda «yoxlanılır» görür və
              nəyinsə alınmadığını düşünüb düyməni yenidən basır.
            */}
            <strong>Ödəniş yoxlanılır</strong> — bir neçə saniyəyə yenilənəcək.
            <strong> İkinci dəfə ödəməyin</strong> — kartınızdan məbləğ
            çıxıbsa, abunə təsdiq gələn kimi öz-özünə uzanır. Bu pəncərəni
            bağlaya bilərsiniz, nəticə dəyişməyəcək.
          </>
        )}
      </div>
      <button
        type="button"
        className="btn-text pay-toast-close"
        onClick={() => setPay(null)}
        aria-label="Bildirişi bağla"
      >
        Bağla
      </button>
    </div>
  ) : null;

  // ⚠️ Bərpa ekranı sessiya yoxlamasından ƏVVƏL gəlir.
  //
  // Token özü kifayət qədər sübutdur — giriş etmiş olmaq tələb edilmir.
  // Əksinə: şifrəsini unutmuş adamın onsuz da sessiyası olmur, e-poçt
  // təsdiqi linkini isə başqa cihazda açmaq tamamilə normaldır.
  if (recover) {
    return (
      <Recover
        mode={recover}
        onDone={() => {
          setRecover(null);
          // Şifrə dəyişəndə köhnə sessiyalar serverdə ləğv olunur, ona görə
          // vəziyyəti sıfırdan qururuq — köhnə istifadəçi obyekti qalmasın.
          setState({ status: 'anonymous', view: 'login' });
        }}
      />
    );
  }

  if (state.status === 'loading') {
    return (
      <div className="boot">
        <div className="spinner" aria-label="Yüklənir" />
      </div>
    );
  }

  if (state.status === 'anonymous') {
    // ⚠️ ZOLAQ BURADA DA GÖSTƏRİLİR. 3DS uzun çəkəndə sessiya bitmiş ola
    // bilər: müştəri ödəyib giriş ekranına düşür. «Ödəniş qeydə alındı»
    // cümləsini görməsə, pulunun itdiyini düşünür.
    const view =
      state.view === 'forgot' ? (
        <Recover
          mode={{ kind: 'forgot' }}
          onDone={() => setState({ status: 'anonymous', view: 'login' })}
        />
      ) : state.view === 'register' ? (
        <Register
          onDone={handleAuthed}
          onSwitch={() => setState({ status: 'anonymous', view: 'login' })}
        />
      ) : (
        <Login
          onLogin={handleAuthed}
          onSwitch={() => setState({ status: 'anonymous', view: 'register' })}
          onForgot={() => setState({ status: 'anonymous', view: 'forgot' })}
        />
      );

    return (
      <>
        {payBanner}
        {view}
      </>
    );
  }

  // Poçt qutusu olmayan hesab (təzə qeydiyyat) poçt interfeysini görməməlidir —
  // görəcəyi şey yoxdur. Əvəzinə növbəti addımlar göstərilir.
  /*
    ⚠️ ÖDƏNİŞ EDİLMƏYİBSƏ PANELƏ GİRİŞ YOXDUR — YALNIZ QURULMA VƏ ÖDƏNİŞ.

    Sahibin qərarı (2026-08-20): «ödəniş yapmasa sistemdə heç bir mesajı
    oxuya bilməz və heç bir yerə girə bilməz». Əvvəl `unpaid` kirayəçi
    bütün paneli görürdü: poçt ekranı, ayarlar, idarə — hamısı açıq idi,
    sadəcə poçt AXINI bağlı idi. Nəticədə müştəri işlək görünən, amma
    işləməyən sistemlə üz-üzə qalırdı.

    İndi belə hesab Onboarding axınında qalır və axın ödənişlə bitir.
    Ödəniş keçən kimi status `active` olur, bu şərt sönür və panel açılır.

    ⚠️ BU, YALNIZ GÖRÜNÜŞDÜR — ƏSL QORUMA SERVERDƏDİR (`requirePaid`).
    Panel gizlətməklə kifayətlənsək, sorğunu birbaşa göndərən adam
    məktubları oxuya bilərdi.

    ⚠️ `past_due` VƏ `trial` BURAYA DÜŞMÜR. `past_due` əvvəl ödəyib və
    möhlət dövründədir — onun poçtunu kəsmək sənədləşdirilmiş davranışa
    ziddir (docs/17). `trial` isə köhnə hesablardır; onları geriyə dönük
    bağlamaq xəbərdarlıqsız kəsmə olardı.
  */
  const odenisGozlenilir = !['active', 'past_due', 'trial'].includes(
    state.user.tenantStatus,
  );

  if (!state.user.mailboxEmail || odenisGozlenilir) {
    return (
      <>
        {payBanner}
        <Onboarding
          user={state.user}
          onLogout={handleLogout}
          onMailboxCreated={handleMailboxCreated}
        />
      </>
    );
  }

  return (
    <>
      {payBanner}
      <Mail
        user={state.user}
        onLogout={handleLogout}
        onProfileChanged={handleMailboxCreated}
      />
    </>
  );
}
